import * as React from 'react';
import {FormEvent} from 'react';
import {
  calculateProgress,
  PrimaryActionButtonProps,
  primaryActionButtonProps,
  SecondaryActionButtonProps,
  secondaryActionButtonProps,
} from './utils';
import * as registrationMachine from '../machines/accountRegistration';
import lang from '../lang';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';

type Props = {
  registrationMachine: UseMachineType;
  isGenericHub: boolean;
};

type ScreenProps = Props & {
  lang: typeof lang.linkPractice;
  onFormSubmit: (e?: FormEvent) => void;
  progress: number;
  actionButtons: {
    primary: PrimaryActionButtonProps;
    secondary: SecondaryActionButtonProps;
  };
  cardNumberInputProps: {
    value: string | null;
    onChange: (cardNumber: string | null) => unknown;
    error: string | undefined;
  };
  businessCodeInputProps:
    | undefined
    | {
        value: string | null;
        onChange: (code: string | null) => unknown;
        error: string | undefined;
      };
};

export type LinkPracticeScreenType = React.ComponentType<ScreenProps>;

export function LinkPractice(
  props: Props & {children: LinkPracticeScreenType}
) {
  const [
    {
      context,
      context: {
        navigator: {location},
      },
      matches,
    },
    send,
  ] = props.registrationMachine;

  const isConnectingRewardsCard = matches({
    [registrationMachine.RegistrationState.linkPractice]:
      registrationMachine.RegistrationState.connectingRewardsCard,
  });
  const isConnectingBusinessCode = matches({
    [registrationMachine.RegistrationState.linkPractice]:
      registrationMachine.RegistrationState.connectingBusinessCode,
  });

  const progress = calculateProgress({
    location,
    draftAccount: context.account,
  });

  const actionButtons = {
    primary: primaryActionButtonProps({
      id: 'rewards_card',
      children: lang.linkPractice.buttons.primary.title,
      loading: isConnectingRewardsCard,
      disabled: !context.cardNumber && !context.businessCode,
    }),
    secondary: secondaryActionButtonProps({
      children: props.isGenericHub
        ? lang.linkPractice.buttons.secondary.title.generic.full
        : lang.linkPractice.buttons.secondary.title.default,
      loading: !props.isGenericHub && isConnectingBusinessCode,
      disabled: isConnectingRewardsCard || isConnectingBusinessCode,
    }),
  };

  const renderProps: ScreenProps = {
    ...props,
    lang: lang.linkPractice,
    onFormSubmit: e => {
      e?.preventDefault();

      if (context.cardNumber) {
        return send(registrationMachine.RegistrationEvent.CONNECT_REWARDS_CARD);
      }

      send(registrationMachine.RegistrationEvent.CONNECT_BUSINESS_CODE);
    },
    progress,
    actionButtons,
    cardNumberInputProps: {
      value: context.cardNumber,
      onChange: cardNumber => {
        send({
          type: registrationMachine.RegistrationEvent.UPDATE_DATA,
          payload: {cardNumber},
        });
      },
      error:
        context.validationErrors?.cardNumber ||
        context.validationErrors?.inviteCode,
    },
    businessCodeInputProps: !props.isGenericHub
      ? undefined
      : {
          value: context.businessCode,
          onChange: businessCode => {
            send({
              type: registrationMachine.RegistrationEvent.UPDATE_DATA,
              payload: {businessCode},
            });
          },
          error: context.validationErrors?.businessCode,
        },
  };

  const Screen = props.children;
  return <Screen {...renderProps} />;
}
